'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_RN_SHADOW_STATE,
  rnShadowPreviewCss,
  rnShadowStyleObject,
  type RnShadowState,
} from '@/lib/browser-rnshadow-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserReactNativeShadowGenerator() {
  const [state, setState] = useState<RnShadowState>(DEFAULT_RN_SHADOW_STATE);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const output = useMemo(() => rnShadowStyleObject(state), [state]);
  const previewShadow = useMemo(() => rnShadowPreviewCss(state), [state]);

  function update<K extends keyof RnShadowState>(key: K, value: RnShadowState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">React Native</span>
        <h2 className="tool-live-title">React Native shadow generator</h2>
        <p className="tool-live-subtitle">
          Dial in an iOS shadow and Android elevation with sliders, preview it live, and copy a
          ready StyleSheet object.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Shadow color</span>
              <input
                onChange={(e) => update('shadowColor', e.target.value)}
                type="color"
                value={state.shadowColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Offset X: {state.offsetX}</span>
              <input
                max={40}
                min={-40}
                onChange={(e) => update('offsetX', Number(e.target.value))}
                type="range"
                value={state.offsetX}
              />
            </label>
            <label className="tool-live-field">
              <span>Offset Y: {state.offsetY}</span>
              <input
                max={40}
                min={-40}
                onChange={(e) => update('offsetY', Number(e.target.value))}
                type="range"
                value={state.offsetY}
              />
            </label>
            <label className="tool-live-field">
              <span>Opacity: {state.opacity}</span>
              <input
                max={1}
                min={0}
                onChange={(e) => update('opacity', Number(e.target.value))}
                step={0.05}
                type="range"
                value={state.opacity}
              />
            </label>
            <label className="tool-live-field">
              <span>Radius: {state.radius}</span>
              <input
                max={40}
                min={0}
                onChange={(e) => update('radius', Number(e.target.value))}
                type="range"
                value={state.radius}
              />
            </label>
            <label className="tool-live-field">
              <span>Elevation (Android): {state.elevation}</span>
              <input
                max={24}
                min={0}
                onChange={(e) => update('elevation', Number(e.target.value))}
                type="range"
                value={state.elevation}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 220,
              background: '#f8fafc',
            }}
          >
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 20,
                background: '#ffffff',
                boxShadow: previewShadow,
              }}
            />
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>RN StyleSheet</span>
            <textarea readOnly rows={12} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Preview approximates iOS shadow rendering.
          </p>
        </section>
      </div>
    </div>
  );
}
